import { BasicMantineTopToolbar } from '@dealstack/frontend/shared/components/BasicMantineTable/BasicMantineTopToolbar';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable';
import { MantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { RecordAny } from '@dealstack/shared/lib/common/common';
import { OnChangeFn, Row, RowSelectionState, Table, TableState } from '@tanstack/react-table';
import { CSSProperties } from 'react';
import { BulkActionGroup } from '@dealstack/frontend/shared/components/Table/MantineTable/ActionsButton/ActionsButton.types';
import { QuickFilterOption } from '@dealstack/frontend/shared/components/Table/MantineTable/QuickFiltersSelect/QuickFiltersSelect.types';

interface Props<TData extends RecordAny> {
	columns: ExtendedColumnDef<TData>[];
	data?: TData[];
	rowCount?: number;
	dataOperations: MantineTableDataOperations;
	customActions?: JSX.Element;
	isLoading?: boolean;
	hasNoTopToolbar?: boolean;
	enableSearchBar?: boolean;
	enableRowActions?: boolean;
	onRowClick?: (row: Row<TData>) => void;
	dataTestId?: string;
	/** Per-row `data-testid`; overrides the table's index-suffixed default. */
	rowDataTestId?: (row: Row<TData>) => string;
	enableExpanding?: boolean;
	enableExpandAll?: boolean;
	enableTopToolbar?: boolean;
	enablePagination?: boolean;
	enableRowSelection?: boolean;
	enableStickyHeader?: boolean;
	enableStickyFooter?: boolean;
	enableRenderBottomToolbarCustomActions?: boolean;
	onRowSelectionChange?: OnChangeFn<RowSelectionState>;
	state?: Partial<TableState>;
	renderBulkActionsMenu?: boolean;
	actions?: (table: Table<TData>) => BulkActionGroup<TData>;
	getRowId?: (row: TData, index: number) => string;
	mantineTableBodyRowPropsStyle?: CSSProperties;
	quickFilterOptions?: QuickFilterOption[];
	onActionCanceled?: () => void;
	actionResetRef?: React.MutableRefObject<(() => void) | null>;
	manualSorting?: boolean;
	enableColumnResizing?: boolean;
	pinnedColumnIds?: string[];
	initialShowColumnFilters?: boolean;
	initialColumnVisibility?: Record<string, boolean>;
}

export const DocumentTable = <T extends RecordAny>({
	columns,
	data,
	customActions,
	isLoading,
	hasNoTopToolbar,
	enableSearchBar,
	enableRowActions,
	dataOperations,
	onRowClick,
	dataTestId,
	rowDataTestId,
	rowCount,
	enableExpanding,
	enableExpandAll,
	enableTopToolbar,
	enableRowSelection = true,
	enablePagination = true,
	enableStickyHeader = true,
	enableStickyFooter = true,
	onRowSelectionChange,
	renderBulkActionsMenu = false,
	state,
	enableRenderBottomToolbarCustomActions = true,
	actions,
	getRowId,
	mantineTableBodyRowPropsStyle,
	quickFilterOptions = [],
	onActionCanceled,
	actionResetRef,
	manualSorting,
	enableColumnResizing = false,
	pinnedColumnIds,
	initialShowColumnFilters = false,
	initialColumnVisibility,
}: Props<T>) => {
	return (
		<MantineTable<T>
			getRowId={getRowId}
			dataTestId={dataTestId}
			enableStickyHeader={enableStickyHeader}
			enableStickyFooter={enableStickyFooter}
			columns={columns}
			state={state}
			enableTopToolbar={enableTopToolbar}
			enableRowSelection={enableRowSelection}
			manualSorting={manualSorting}
			onRowSelectionChange={onRowSelectionChange as OnChangeFn<RowSelectionState>}
			isLoading={isLoading}
			renderBulkActionsMenu={renderBulkActionsMenu}
			actions={actions}
			data={data ?? []}
			rowCount={rowCount}
			enableRowActions={enableRowActions}
			enableSearchBar={enableSearchBar}
			dataOperations={dataOperations}
			enablePagination={enablePagination}
			manualPagination={enablePagination}
			renderTopToolbar={
				hasNoTopToolbar
					? null
					: ({ table }) => (
							<BasicMantineTopToolbar
								table={table}
								customActions={customActions}
								hideSearchBar={enableSearchBar === false}
							/>
						)
			}
			quickFilterOptions={quickFilterOptions}
			mantineTableBodyRowProps={({ row }) => ({
				onClick: () => {
					onRowClick?.(row);
				},
				style: mantineTableBodyRowPropsStyle,
				...(rowDataTestId && { 'data-testid': rowDataTestId(row) }),
			})}
			enableExpandAll={enableExpandAll}
			enableExpanding={enableExpanding}
			enableRenderBottomToolbarCustomActions={enableRenderBottomToolbarCustomActions}
			onActionCanceled={onActionCanceled}
			actionResetRef={actionResetRef}
			enableColumnResizing={enableColumnResizing}
			initialState={{
				showColumnFilters: initialShowColumnFilters,
				...(initialColumnVisibility && { columnVisibility: initialColumnVisibility }),
			}}
			pinnedColumnIds={pinnedColumnIds}
		/>
	);
};
